import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { attributionApi, type AttributionSummary } from "@/api/attribution.ts";

function ConvRate({ signups, conversions }: { signups: number; conversions: number }) {
  const rate = signups > 0 ? ((conversions / signups) * 100).toFixed(1) : "0.0";
  return <span className="text-surface-500">{rate}%</span>;
}

export function AdminAttributionPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "attribution", days],
    queryFn: () => attributionApi.summary({ days }),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const summary: AttributionSummary | undefined = data;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-surface-400" />
          <h1 className="text-xl font-semibold text-surface-900">Attribution</h1>
        </div>
        <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))} className="w-32" options={[{value:"7",label:"Last 7 days"},{value:"30",label:"Last 30 days"},{value:"90",label:"Last 90 days"}]} />
      </div>

      {!summary ? (
        <Card><CardContent className="py-8 text-center text-surface-400 text-sm">No attribution data yet.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {/* By Source */}
          <Card>
            <CardHeader><CardTitle className="text-sm">By Source</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="text-left px-4 py-2 text-xs font-medium text-surface-500">Source</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Signups</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Conv.</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.bySource.map((r) => (
                    <tr key={r.source} className="border-b border-surface-50">
                      <td className="px-4 py-2 text-surface-700">{r.source}</td>
                      <td className="px-4 py-2 text-right text-surface-900 font-medium">{r.signups}</td>
                      <td className="px-4 py-2 text-right text-emerald-600 font-medium">{r.conversions}</td>
                      <td className="px-4 py-2 text-right"><ConvRate signups={r.signups} conversions={r.conversions} /></td>
                    </tr>
                  ))}
                  {summary.bySource.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-surface-400 text-xs">No data</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* By Campaign */}
          <Card>
            <CardHeader><CardTitle className="text-sm">By Campaign</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="text-left px-4 py-2 text-xs font-medium text-surface-500">Campaign</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Signups</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Conv.</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byCampaign.map((r) => (
                    <tr key={r.campaign} className="border-b border-surface-50">
                      <td className="px-4 py-2 text-surface-700">{r.campaign}</td>
                      <td className="px-4 py-2 text-right text-surface-900 font-medium">{r.signups}</td>
                      <td className="px-4 py-2 text-right text-emerald-600 font-medium">{r.conversions}</td>
                      <td className="px-4 py-2 text-right"><ConvRate signups={r.signups} conversions={r.conversions} /></td>
                    </tr>
                  ))}
                  {summary.byCampaign.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-surface-400 text-xs">No data</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* By Landing Variant */}
          <Card>
            <CardHeader><CardTitle className="text-sm">By Landing Variant</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="text-left px-4 py-2 text-xs font-medium text-surface-500">Variant</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Signups</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Conv.</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byVariant.map((r) => (
                    <tr key={r.variant} className="border-b border-surface-50">
                      <td className="px-4 py-2 text-surface-700">{r.variant}</td>
                      <td className="px-4 py-2 text-right text-surface-900 font-medium">{r.signups}</td>
                      <td className="px-4 py-2 text-right text-emerald-600 font-medium">{r.conversions}</td>
                      <td className="px-4 py-2 text-right"><ConvRate signups={r.signups} conversions={r.conversions} /></td>
                    </tr>
                  ))}
                  {summary.byVariant.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-surface-400 text-xs">No data</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* By Referrer */}
          <Card>
            <CardHeader><CardTitle className="text-sm">By Referrer Domain</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="text-left px-4 py-2 text-xs font-medium text-surface-500">Referrer</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-surface-500">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byReferrer.map((r) => (
                    <tr key={r.referrer} className="border-b border-surface-50">
                      <td className="px-4 py-2 text-surface-700 font-mono text-xs">{r.referrer}</td>
                      <td className="px-4 py-2 text-right text-surface-900 font-medium">{r.signups}</td>
                    </tr>
                  ))}
                  {summary.byReferrer.length === 0 && (
                    <tr><td colSpan={2} className="px-4 py-4 text-center text-surface-400 text-xs">No data</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
